import React, { useMemo } from 'react'

import { Filter } from '@cubejs-client/core'
import { isEmpty } from 'ramda'

import { Flex, StatusBadge, Table } from '@driftt/dds'
import { ChartCard, TypeFormats } from '@driftt/dds.reports'

import { SchedulingEventSummaryMetrics } from 'api/report/types'
import ExportButton from 'components/core/ExportButton'
import LeadCell from 'components/core/LeadCell'
import {
  SOURCE_HUMANIZED_TRANSLATION,
  STATUS_HUMANIZED_TRANSLATION,
} from 'constants/meetings'
import useQueryConfig from 'hooks/useQueryConfig'
import useSortConfig from 'hooks/useSortConfig'
import { userHasOrgConversationReports } from 'utils/access'
import { createCSVDownload, csvFilenameFromQuery } from 'utils/csv'

import { toCSV } from './formatMeetingExport'
import useMeetingsBooked from './useMeetingsBooked.hook'

export interface ChartProps {
  metric: string
  filters?: Filter[]
}

const STATUS_BADGE_VARIANT = {
  active: 'pending',
  canceled: 'error',
  deleted: 'error',
  no_show: 'error',
  rescheduled: 'pending',
}

const renderStatusCell = (status) => {
  const statusTextLowerCase = status.toLowerCase()
  const displayText = STATUS_HUMANIZED_TRANSLATION[status]

  return (
    <StatusBadge
      size="medium"
      variant={STATUS_BADGE_VARIANT[statusTextLowerCase]}
    >
      {displayText}
    </StatusBadge>
  )
}

const WhoBookedMeetingsChart = ({ metric, filters = [] }: ChartProps) => {
  const { queryConfig } = useQueryConfig(metric)

  const [sortConfig] = useSortConfig(SchedulingEventSummaryMetrics.CREATED_AT)
  const {
    handleTableStateChange,
    partialMetric: paginatedMeetingsData,
    fullMetric: meetingsData,
    totalRowCount,
    pagination,
    isLoading,
    isError,
  } = useMeetingsBooked(sortConfig, queryConfig, filters)

  const subject = useMemo(
    () => (userHasOrgConversationReports() ? 'us' : 'you'),
    []
  )

  const onExportClick = () => {
    createCSVDownload(toCSV(meetingsData), csvFilenameFromQuery(queryConfig))
  }

  return (
    <ChartCard
      css={{
        paddingX: 0,
        paddingBottom: 0,
      }}
    >
      <Flex
        fullWidth
        grow={1}
        css={{
          paddingX: '$3',
        }}
      >
        <ChartCard.Header
          title="Recently booked"
          subtitle={`Who booked a meeting with ${subject} most recently?`}
          action={
            <ExportButton
              isDisabled={isEmpty(meetingsData)}
              onClick={onExportClick}
              queryConfig={queryConfig}
            />
          }
        />
      </Flex>
      <ChartCard.Content>
        {!isError && (
          <Table
            disableBoxShadow
            isLoading={isLoading}
            rows={paginatedMeetingsData ?? []}
            columns={[
              {
                value: 'endUserId',
                header: 'Name',
                cell: ({ endUser }) => <LeadCell endUser={endUser} />,
              },
              {
                value: 'endUser.company',
                header: 'Company',
                cell: ({ endUser }) => endUser.company || 'N/A',
              },
              {
                header: 'Booked with',
                value: 'agentName',
              },
              {
                header: 'Booked on',
                value: 'createdAt',
                cell: ({ createdAt }) => TypeFormats.DATE(new Date(createdAt)),
              },
              {
                header: 'Booked for',
                value: 'meetingStartAt',
                cell: ({ meetingStartAt }) =>
                  TypeFormats.DATE(new Date(meetingStartAt)),
              },
              {
                header: 'Source',
                value: 'meetingSource',
                cell: ({ meetingSource }) =>
                  SOURCE_HUMANIZED_TRANSLATION[meetingSource] ?? 'N/A',
              },
              {
                header: 'Status',
                value: 'status',
                cell: ({ status }) => renderStatusCell(status),
              },
            ]}
            onStateChange={handleTableStateChange}
            pagination={{
              rowTotal: totalRowCount ?? 0,
              defaultPagination: pagination,
            }}
          />
        )}
      </ChartCard.Content>
    </ChartCard>
  )
}

export default WhoBookedMeetingsChart
